import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Check if token is expired before making API call
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        setUser(null);
        setLoading(false);
        return;
      }
    } catch {
      // If token can't be decoded, let the API validate it
    }

    let retries = 2;
    for (let i = 0; i <= retries; i++) {
      try {
        const response = await getMe();
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        const status = error.response?.status;
        
        if (status === 401) {
          // Token invalid/expired - logout
          localStorage.removeItem('token');
          setUser(null);
          break;
        }
        
        if (status === 403) {
          // Subscription issue - user is still authenticated, just restricted
          // Try to get basic user data anyway
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            setUser({ id: payload.sub, email: payload.email || '', role: payload.role || 'user', subscription_status: 'expired' });
          } catch {
            // Fallback - keep user null but don't remove token
          }
          break;
        }
        
        if (status >= 500 && i < retries) {
          // Server error - retry
          await new Promise(r => setTimeout(r, 500 * (i + 1)));
          continue;
        }
        
        if (!error.response && i < retries) {
          // Network error - retry
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
          continue;
        }

        // After all retries, don't remove token for network/server errors
        // User might just have connectivity issues
        if (!status || status >= 500) {
          break;
        }
        
        // Other client errors - remove token
        localStorage.removeItem('token');
        setUser(null);
        break;
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Periodic token validation (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            localStorage.removeItem('token');
            setUser(null);
          }
        } catch {
          // Token is malformed
        }
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loginUser = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logoutUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
